import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

import type { ShareMenuItem } from "../../components/share/ShareMenu.js";
import { toast } from "../../components/ui/use-toast.js";
import { documentAbsoluteUrl, documentMailtoHref, triggerMailto, type ShareableDoc } from "./shareLinks.js";

/**
 * The canonical Share trio for a documentation file — **Share via email**
 * (mailto), **Share to chat**, **Copy link** — as `ShareMenuItem[]`. ONE source
 * of the actions so the doc reader (`<ShareMenu>`) and the list kebab
 * (`RowActions`, which maps these into its `<Menu>`) stay identical: same
 * labels, icons, and `testId`s. "Share to chat" needs a dialog, so the caller
 * passes `onShareToChat` (open it); the other two are self-contained.
 *
 * Read-tier: every action is safe for anyone who can VIEW the doc — a mailto
 * draft, a clipboard copy, and a non-monetary chat summary + in-app link.
 */
export function buildDocShareItems({
  doc,
  onShareToChat,
}: {
  doc: ShareableDoc;
  onShareToChat: () => void;
}): ShareMenuItem[] {
  return [
    {
      key: "email",
      label: "Share via email",
      icon: <EmailOutlinedIcon fontSize="small" />,
      testId: "share-menu-email",
      onClick: () => triggerMailto(documentMailtoHref(doc)),
    },
    {
      key: "chat",
      label: "Share to chat",
      icon: <ChatBubbleOutlineRoundedIcon fontSize="small" />,
      testId: "share-menu-chat",
      onClick: onShareToChat,
    },
    {
      key: "copy",
      label: "Copy link",
      icon: <ContentCopyRoundedIcon fontSize="small" />,
      testId: "share-menu-copy-link",
      onClick: () => {
        void navigator.clipboard
          ?.writeText(documentAbsoluteUrl(doc))
          .then(() => toast({ title: "Link copied", description: "Document link copied to clipboard." }));
      },
    },
  ];
}
